import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import {
  CalendarIcon,
  ChatExclamationmarkIcon,
  ExclamationmarkTriangleIcon,
  HandshakeIcon,
  HeartIcon,
  PersonGroupIcon,
} from '@navikt/aksel-icons';
import { useGetActionPlanFocusCategoryQuery } from '../../../../hooks/queries/useDashboardPublicQueries';
import { getHealthCategoryKey, type HealthCategoryKey } from '../../../../utils/healthCategory';
import { agreementTones, formatScore, getScoreLevel, levelTones } from '../../../../components/AnalysisLayout/analysis.utils';
import { focusSignalsOf } from '../../actionPlanFocusView';
import { ContributionLayout } from '../ContributionLayout/ContributionLayout';
import classes from './ActionPlanFocus.module.css';

const icons: Record<HealthCategoryKey, typeof HeartIcon> = {
  safetyAndOpenness: ChatExclamationmarkIcon,
  recognitionAndAppreciation: HeartIcon,
  trustAndSupport: HandshakeIcon,
  inclusionAndRespect: PersonGroupIcon,
};

interface ActionPlanFocusProps {
  surveyUuid: string;
  onContinue: (categoryId: number) => void;
}

export const ActionPlanFocus = ({ surveyUuid, onContinue }: ActionPlanFocusProps) => {
  const { t, i18n } = useTranslation();
  const { data: focusCategory, isLoading, error } = useGetActionPlanFocusCategoryQuery(surveyUuid);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Når brukeren kommer tilbake fra steg 2, starter skjermleseren på overskriften
  useEffect(() => {
    if (focusCategory) headingRef.current?.focus({ preventScroll: true });
  }, [focusCategory]);

  if (isLoading) {
    return (
      <ContributionLayout step={1}>
        <div aria-busy="true" className={classes.loading}>
          <span className={classes.srOnly}>{t('publicSurvey.actionPlan.focus.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={360} />
        </div>
      </ContributionLayout>
    );
  }

  if (error || !focusCategory) {
    return null;
  }

  const { score } = focusCategory;
  const { hasLowScore, disagrees, why: whyKey } = focusSignalsOf(focusCategory);
  // The score alone, as on the results page and the team dashboard
  const tone = levelTones[getScoreLevel(score)];
  const key = getHealthCategoryKey(focusCategory.categoryName);
  const Icon = key ? icons[key] : HeartIcon;
  // Skalaen går fra 1 til 5
  const width = `${Math.min(100, Math.max(0, ((score - 1) / 4) * 100))}%`;
  const why = whyKey ? t(`contribution.focus.why.${whyKey}`) : focusCategory.reason;

  const nextSurveyDate =
    focusCategory.nextSurveyDate && new Date(focusCategory.nextSurveyDate) > new Date()
      ? new Date(focusCategory.nextSurveyDate).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'nb-NO', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : null;

  return (
    <ContributionLayout step={1}>
      <div className={classes.focus}>
        <div className={classes.intro}>
          <Heading ref={headingRef} tabIndex={-1} level={1} data-size="xl" className={classes.title}>
            {t('contribution.focus.title')}
          </Heading>
          <Paragraph className={classes.lead}>{t('contribution.focus.intro')}</Paragraph>
        </div>

        <section aria-labelledby="focus-area-heading" className={classes.card}>
          <div className={classes.cardBody}>
            <div className={classes.areaRow}>
              <div className={classes.area}>
                <span aria-hidden="true" className={classes.areaIcon}>
                  <Icon />
                </span>
                <Heading id="focus-area-heading" level={2} data-size="md" className={classes.areaName}>
                  {focusCategory.categoryName}
                </Heading>
              </div>
              <p className={classes.score}>
                <span className={classes.scoreValue}>{formatScore(score, 2)}</span>
                <span className={classes.subtle}>{t('publicResults.of5')}</span>
              </p>
            </div>
            <span aria-hidden="true" className={classes.track}>
              <span className={classes.fill} data-tone={tone} style={{ width }} />
            </span>

            {(hasLowScore || disagrees) && (
              <dl className={classes.signals}>
                {hasLowScore && (
                  <div className={classes.signal} data-tone="warning">
                    <ExclamationmarkTriangleIcon aria-hidden className={classes.signalIcon} />
                    <div>
                      <dt>{t('contribution.focus.lowScore')}</dt>
                      <dd>{t('contribution.focus.lowScoreText', { score: formatScore(score) })}</dd>
                    </div>
                  </div>
                )}
                {disagrees && (
                  <div className={classes.signal} data-tone={agreementTones.LOW}>
                    <PersonGroupIcon aria-hidden className={classes.signalIcon} />
                    <div>
                      <dt>{t('teamResult.agreement.LOW')}</dt>
                      <dd>{t('teamResult.agreementForTeam.LOW')}</dd>
                    </div>
                  </div>
                )}
              </dl>
            )}

            <Paragraph>{why}</Paragraph>
            {nextSurveyDate && (
              <Paragraph data-size="sm" className={classes.nextSurvey}>
                <CalendarIcon aria-hidden />
                {t('publicSurvey.actionPlan.focus.nextSurvey', { date: nextSurveyDate })}
              </Paragraph>
            )}
          </div>
          <div className={classes.cardFooter}>
            <Paragraph data-size="sm" className={classes.next}>
              {t('contribution.focus.next')}
            </Paragraph>
            <Button onClick={() => onContinue(focusCategory.categoryId)}>
              {t('publicSurvey.actionPlan.focus.button')}
            </Button>
          </div>
        </section>
      </div>
    </ContributionLayout>
  );
};
